import { useLiveQuery } from "dexie-react-hooks";
import {
  Archive, Bell, Calendar, CalendarDays, ChevronRight, CircleCheck, History, Inbox, LayoutGrid, Repeat, Settings, Tag, Timer, Trash2, TrendingUp, type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toISODate, type ActivityEvent } from "@todoer/shared";
import { activityTarget, describeActivity } from "../lib/activity.js";
import { api } from "../lib/api.js";
import { db, useCollaborators, useUser } from "../lib/db.js";
import { relativeTime } from "../lib/format.js";
import { sortProjectTree, useNow } from "../lib/hooks.js";
import { onRemoteChange, subscribe, type SyncState } from "../lib/sync.js";
import { openTaskParams } from "./TaskList.js";
import { Popover, usePopover } from "./overlay.js";
import { Avatar, cx, EmptyState, IconButton } from "./ui.js";

/** Calendar icon with today's date inside, like Things and Todoist. */
export function TodayIcon({ size = 18, className }: { size?: number; className?: string }) {
  const day = useNow().getDate();
  return (
    <span className={cx("relative inline-grid shrink-0 place-items-center", className)} style={{ width: size, height: size }} aria-hidden>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="lucide">
        <rect x="3" y="4" width="18" height="18" rx="3" /><path d="M8 2v4M16 2v4M3 9h18" />
      </svg>
      <span className="tabular absolute inset-x-0 text-center font-bold leading-none" style={{ top: size * 0.47, fontSize: Math.round(size * 0.42) }}>{day}</span>
    </span>
  );
}

export interface NavDef { to: string; label: string; icon: LucideIcon | "today"; shortcut?: string; countKey?: "inbox" | "today" }
export const NAV_PRIMARY: NavDef[] = [
  { to: "/inbox", label: "Inbox", icon: Inbox, shortcut: "G I", countKey: "inbox" },
  { to: "/today", label: "Today", icon: "today", shortcut: "G T", countKey: "today" },
  { to: "/upcoming", label: "Upcoming", icon: CalendarDays, shortcut: "G U" },
  { to: "/calendar", label: "Calendar", icon: Calendar, shortcut: "G C" },
  { to: "/filters", label: "Filters & Labels", icon: Tag, shortcut: "G F" },
  { to: "/completed", label: "Completed", icon: CircleCheck },
];
export const NAV_TOOLS: NavDef[] = [
  { to: "/matrix", label: "Matrix", icon: LayoutGrid, shortcut: "G M" },
  { to: "/habits", label: "Habits", icon: Repeat, shortcut: "G H" },
  { to: "/focus", label: "Focus", icon: Timer, shortcut: "G O" },
  { to: "/productivity", label: "Productivity", icon: TrendingUp, shortcut: "G P" },
];
export const NAV_FOOTER: NavDef[] = [
  { to: "/activity", label: "Activity", icon: History },
  { to: "/archived", label: "Archived projects", icon: Archive },
  { to: "/trash", label: "Trash", icon: Trash2 },
  { to: "/settings", label: "Settings", icon: Settings, shortcut: "G S" },
];

export function NavIcon({ icon, size = 18 }: { icon: NavDef["icon"]; size?: number }) {
  if (icon === "today") return <TodayIcon size={size} />;
  const I = icon;
  return <I size={size} aria-hidden />;
}

/** Projects, labels, filters and open-task counts for navigation. */
export function useNavData() {
  const today = toISODate(useNow());
  const data = useLiveQuery(async () => {
    const [projects, labels, filters, active] = await Promise.all([
      db.projects.toArray(), db.labels.toArray(), db.filters.toArray(), db.tasks.filter((t) => !t.completedAt).toArray(),
    ]);
    return { projects, labels, filters, active };
  }, []);
  return useMemo(() => {
    const byProject = new Map<string, number>();
    let todayCount = 0;
    for (const t of data?.active ?? []) {
      if (!t.parentId) byProject.set(t.projectId, (byProject.get(t.projectId) ?? 0) + 1);
      if (t.due && t.due.date <= today) todayCount++;
    }
    const inbox = data?.projects.find((p) => p.isInbox);
    const tree = sortProjectTree((data?.projects ?? []).filter((p) => !p.isInbox && !p.isArchived));
    return {
      loaded: !!data,
      tree,
      inbox,
      counts: { byProject, today: todayCount, inbox: inbox ? byProject.get(inbox.id) ?? 0 : 0 },
      favProjects: tree.filter((p) => p.isFavorite),
      favLabels: (data?.labels ?? []).filter((l) => l.isFavorite),
      favFilters: (data?.filters ?? []).filter((f) => f.isFavorite),
    };
  }, [data, today]);
}

export function useSyncState() {
  const [s, setS] = useState<SyncState | null>(null);
  useEffect(() => subscribe(setS), []);
  return s;
}

export function SyncStatus({ className }: { className?: string }) {
  const sync = useSyncState();
  const state = !sync?.online ? { dot: "bg-text-faint", text: "Offline" }
    : sync.syncing ? { dot: "bg-p2 animate-pulse", text: "Syncing…" }
      : sync.lastError ? { dot: "bg-danger", text: "Sync error" }
        : { dot: "bg-success", text: "Synced" };
  return (
    <span className={cx("inline-flex items-center gap-1.5 text-meta text-text-faint", className)} title={sync?.lastError ?? undefined} role="status">
      <span aria-hidden className={cx("h-1.5 w-1.5 rounded-full", state.dot)} />{state.text}
    </span>
  );
}

/** Teammates' recent activity in shared projects. Each row opens its task or project. */
export function NotificationsButton({ side = "bottom" }: { side?: "bottom" | "right" }) {
  const pop = usePopover<HTMLButtonElement>();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [seen, setSeen] = useState<number>(() => { try { return Number(localStorage.getItem("todoer.seenActivity") ?? 0); } catch { return 0; } });
  const people = useCollaborators();
  const projects = useLiveQuery(() => db.projects.toArray(), [], []);
  const user = useUser();
  const [, setParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => { api<ActivityEvent[]>("/api/activity?others=1&limit=20").then(setEvents).catch(() => {}); };
    load();
    return onRemoteChange(load);
  }, []);

  const unread = events.filter((e) => e.id > seen).length;
  const toggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    pop.toggle(e);
    const max = events[0]?.id ?? seen;
    setSeen(max);
    try { localStorage.setItem("todoer.seenActivity", String(max)); } catch { /* ignore */ }
  };
  const go = (e: ActivityEvent) => {
    pop.close();
    const target = activityTarget(e);
    if (target?.kind === "task") setParams(openTaskParams(target.id));
    else if (target?.kind === "project") navigate(projects.find((p) => p.id === target.id)?.isInbox ? "/inbox" : `/project/${target.id}`);
  };

  return (
    <>
      <IconButton icon={Bell} label={`Notifications${unread ? `, ${unread} new` : ""}`} onClick={toggle} active={pop.isOpen}
        badge={unread > 0 && <span className="tabular absolute right-0.5 top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand-strong px-1 text-[10px] font-bold leading-none text-white">{unread}</span>} />
      {pop.anchor && (
        <Popover anchor={pop.anchor} onClose={pop.close} title="Notifications" width={360} side={side} align="start">
          <div className="hidden border-b border-border px-4 py-3 desk:block"><h2 className="text-heading font-semibold">Notifications</h2></div>
          {events.length ? (
            <ul className="max-h-[420px] overflow-y-auto py-1 phone:max-h-none">
              {events.map((e) => {
                const d = describeActivity(e, people, projects, user?.id);
                return (
                  <li key={e.id}>
                    <button type="button" onClick={() => go(e)} className="group flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-hover">
                      <Avatar person={people.find((p) => p.id === e.userId) ?? { name: d.who, image: null }} size={28} />
                      <span className="min-w-0 flex-1 text-body">
                        <span className="font-semibold">{d.who}</span> {d.text}
                        <span className="mt-0.5 block text-meta text-text-faint">{d.where && `${d.where} · `}{relativeTime(e.createdAt)}</span>
                      </span>
                      {e.id > seen && <span aria-label="New" className="mt-2 h-2 w-2 shrink-0 rounded-full bg-brand" />}
                      <ChevronRight size={16} className="mt-1.5 shrink-0 text-text-faint opacity-0 group-hover:opacity-100" aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : <EmptyState compact kind="bell" title="All quiet">Activity from teammates in shared projects shows here.</EmptyState>}
        </Popover>
      )}
    </>
  );
}
